import {gql} from '@apollo/client';

import {trackEvent} from 'analytics/Analytics';
import {
  ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME,
  isAntibodyComponentSchemaInterfaceId,
} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {canUseBiologicsFeature} from 'antibodies/util/biologicsCapability';
import {BiologicsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileState from 'filestate/FileState';
import FolderItemState from 'filestate/FolderItemState';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import APIUtil from 'util/APIUtil';
import {Optional} from 'util/Types';

import {
  AntibodyComponentSchemaInterfacesQuery,
  AntibodyComponentSchemaInterfacesQueryVariables,
} from './__generated/AntibodyComponentSchemaInterfacesQuery';

export type FileUseEventPrefix = 'open' | 'switch';

export const ANTIBODY_COMPONENT_SCHEMA_INTERFACES_QUERY = gql`
  query AntibodyComponentSchemaInterfacesQuery($schemaId: StrictString!)
  @skipAudit
  @engComponent(name: ANTIBODIES) {
    tagSchema(id: $schemaId) {
      ... on TagSchemaNode {
        id
        interfaceConfigs {
          interface {
            id
            ... on StaticSchemaInterfaceNode {
              id
            }
          }
        }
      }
    }
  }
`;

interface MaybeTrackAntibodyComponentOpenArgs {
  eventPrefix: FileUseEventPrefix;
  fileId: string;
  fileState: FileState;
  source: Optional<string>;
}

export async function maybeTrackAntibodyComponentOpen({
  eventPrefix,
  fileId,
  fileState,
  source,
}: MaybeTrackAntibodyComponentOpenArgs): Promise<void> {
  if (source == null) {
    return;
  }

  const fileType = APIUtil.apiIdToType(fileId);
  if (
    ![ModelTypeConstants.SEQUENCE, ModelTypeConstants.PROTEIN, ModelTypeConstants.BASIC_FOLDER_ITEM].includes(
      fileType
    ) ||
    !canUseBiologicsFeature(BiologicsFeature.BIOLOGICS_OPEN) ||
    !(fileState instanceof FolderItemState)
  ) {
    return;
  }

  const schemaId = fileState.model().tagSchema()?.id;
  if (schemaId == null) {
    return;
  }

  const result = await doQuerySafe<
    AntibodyComponentSchemaInterfacesQuery,
    AntibodyComponentSchemaInterfacesQueryVariables
  >({query: ANTIBODY_COMPONENT_SCHEMA_INTERFACES_QUERY}, {variables: {schemaId}, fetchPolicy: 'cache-first'});
  if (isInaccessible(result.tagSchema)) {
    return;
  }

  const antibodyComponentSsi = result.tagSchema.interfaceConfigs
    .map((config) => config.interface.id)
    .find(isAntibodyComponentSchemaInterfaceId);
  if (antibodyComponentSsi == null) {
    return;
  }

  trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.OPENED_ANTIBODY_COMPONENT_ENTITY, {
    schemaId,
    antibody_component_ssi: ANTIBODY_COMPONENT_SCHEMA_INTERFACE_ID_TO_DISPLAY_NAME[antibodyComponentSsi],
    action_detail: eventPrefix,
  });
}
